// AIRadio.jsx
import React, { useState, useRef, useEffect } from "react";
import { Volume2, VolumeX, Play, Pause, Info, Palette, PhoneCall, MessageSquare } from "lucide-react";
import WebSocketManager from "../WebSocketManager";
import AudioUtils from "../utils/AudioUtils";
import VisualizerUtils from "../utils/VisualizerUtils";
import TranscriptPanel from "./ui/TranscriptPanel";

// Themes configuration.
//
// cardBgRgb / surfaceRgb / pageRgb are raw "r,g,b" strings used to compose
// rgba() and gradient values that Tailwind's JIT can't generate from
// dynamic class names (e.g. translucent glass surfaces).
const themes = {
  black: {
    bg: 'bg-black',
    primary: 'bg-zinc-900',
    secondary: 'bg-zinc-800',
    border: 'border-zinc-800',
    text: 'text-white',
    textSecondary: 'text-zinc-400',
    textTertiary: 'text-zinc-600',
    accent: '#ffffff',
    accentRgb: '255,255,255',
    pageRgb: '0,0,0',
    cardBgRgb: '24,24,27',
    surfaceRgb: '39,39,42',
    inactive: 'bg-zinc-700',
    hover: 'hover:bg-zinc-800',
    name: 'Dark Mono',
    djOne: 'bg-red-900/50 text-red-100',
    djTwo: 'bg-green-900/50 text-green-100',
    system: 'bg-blue-900/50 text-blue-100',
    isLight: false,
    dial: 'from-zinc-700 to-zinc-900',
    dialInner: 'from-zinc-800 to-zinc-900',
    dialMark: 'bg-white'
  },
  blue: {
    bg: 'bg-slate-950',
    primary: 'bg-slate-900',
    secondary: 'bg-slate-800',
    border: 'border-slate-800',
    text: 'text-slate-100',
    textSecondary: 'text-slate-400',
    textTertiary: 'text-slate-600',
    accent: '#38bdf8',
    accentRgb: '56,189,248',
    pageRgb: '2,6,23',
    cardBgRgb: '15,23,42',
    surfaceRgb: '30,41,59',
    inactive: 'bg-slate-700',
    hover: 'hover:bg-slate-800',
    name: 'Midnight Blue',
    djOne: 'bg-red-900/50 text-red-100',
    djTwo: 'bg-green-900/50 text-green-100',
    system: 'bg-blue-900/50 text-blue-100',
    isLight: false,
    dial: 'from-slate-700 to-slate-900',
    dialInner: 'from-slate-800 to-slate-900',
    dialMark: 'bg-white'
  },
  purple: {
    bg: 'bg-purple-950',
    primary: 'bg-purple-900',
    secondary: 'bg-purple-800',
    border: 'border-purple-800',
    text: 'text-purple-100',
    textSecondary: 'text-purple-300',
    textTertiary: 'text-purple-500',
    accent: '#d8b4fe',
    accentRgb: '216,180,254',
    pageRgb: '46,16,101',
    cardBgRgb: '88,28,135',
    surfaceRgb: '107,33,168',
    inactive: 'bg-purple-700',
    hover: 'hover:bg-purple-800',
    name: 'Deep Purple',
    djOne: 'bg-red-800/50 text-red-100',
    djTwo: 'bg-green-800/50 text-green-100',
    system: 'bg-blue-800/50 text-blue-100',
    isLight: false,
    dial: 'from-purple-700 to-purple-900',
    dialInner: 'from-purple-800 to-purple-900',
    dialMark: 'bg-white'
  },
  green: {
    bg: 'bg-emerald-950',
    primary: 'bg-emerald-900',
    secondary: 'bg-emerald-800',
    border: 'border-emerald-800',
    text: 'text-emerald-100',
    textSecondary: 'text-emerald-300',
    textTertiary: 'text-emerald-600',
    accent: '#34d399',
    accentRgb: '52,211,153',
    pageRgb: '2,44,34',
    cardBgRgb: '6,78,59',
    surfaceRgb: '6,95,70',
    inactive: 'bg-emerald-700',
    hover: 'hover:bg-emerald-800',
    name: 'Matrix Green',
    djOne: 'bg-red-900/50 text-red-100',
    djTwo: 'bg-teal-900/50 text-teal-100',
    system: 'bg-blue-900/50 text-blue-100',
    isLight: false,
    dial: 'from-emerald-700 to-emerald-900',
    dialInner: 'from-emerald-800 to-emerald-900',
    dialMark: 'bg-white'
  },
  cyber: {
    bg: 'bg-gray-950',
    primary: 'bg-gray-900',
    secondary: 'bg-gray-800',
    border: 'border-pink-900',
    text: 'text-cyan-300',
    textSecondary: 'text-pink-500',
    textTertiary: 'text-gray-500',
    accent: '#f472b6',
    accentRgb: '244,114,182',
    pageRgb: '3,7,18',
    cardBgRgb: '17,24,39',
    surfaceRgb: '31,41,55',
    inactive: 'bg-gray-800',
    hover: 'hover:bg-gray-800',
    name: 'Cyberpunk',
    djOne: 'bg-pink-900/50 text-pink-100',
    djTwo: 'bg-cyan-900/50 text-cyan-100',
    system: 'bg-yellow-900/50 text-yellow-100',
    isLight: false,
    dial: 'from-gray-700 to-gray-900',
    dialInner: 'from-gray-800 to-gray-900',
    dialMark: 'bg-cyan-400'
  },
  light: {
    bg: 'bg-slate-50',
    primary: 'bg-white',
    secondary: 'bg-slate-100',
    border: 'border-slate-200',
    text: 'text-slate-900',
    textSecondary: 'text-slate-600',
    textTertiary: 'text-slate-400',
    accent: '#475569',
    accentRgb: '71,85,105',
    pageRgb: '248,250,252',
    cardBgRgb: '255,255,255',
    surfaceRgb: '241,245,249',
    inactive: 'bg-slate-200',
    hover: 'hover:bg-slate-50',
    name: 'Light Mode',
    djOne: 'bg-red-100 text-red-800',
    djTwo: 'bg-green-100 text-green-800',
    system: 'bg-blue-100 text-blue-800',
    isLight: true,
    dial: 'from-slate-300 to-slate-400',
    dialInner: 'from-slate-400 to-slate-500',
    dialMark: 'bg-slate-800'
  },
  sunset: {
    bg: 'bg-orange-950',
    primary: 'bg-orange-900',
    secondary: 'bg-orange-800',
    border: 'border-orange-800',
    text: 'text-orange-100',
    textSecondary: 'text-orange-300',
    textTertiary: 'text-orange-500',
    accent: '#fb923c',
    accentRgb: '251,146,60',
    pageRgb: '67,20,7',
    cardBgRgb: '124,45,18',
    surfaceRgb: '154,52,18',
    inactive: 'bg-orange-700',
    hover: 'hover:bg-orange-800',
    name: 'Sunset',
    djOne: 'bg-red-900/50 text-red-100',
    djTwo: 'bg-amber-900/50 text-amber-100',
    system: 'bg-yellow-900/50 text-yellow-100',
    isLight: false,
    dial: 'from-orange-700 to-orange-900',
    dialInner: 'from-orange-800 to-orange-900',
    dialMark: 'bg-white'
  },
  rose: {
    bg: 'bg-rose-950',
    primary: 'bg-rose-900',
    secondary: 'bg-rose-800',
    border: 'border-rose-800',
    text: 'text-rose-100',
    textSecondary: 'text-rose-300',
    textTertiary: 'text-rose-500',
    accent: '#fb7185',
    accentRgb: '251,113,133',
    pageRgb: '76,5,25',
    cardBgRgb: '136,19,55',
    surfaceRgb: '159,18,57',
    inactive: 'bg-rose-700',
    hover: 'hover:bg-rose-800',
    name: 'Rose',
    djOne: 'bg-pink-900/50 text-pink-100',
    djTwo: 'bg-fuchsia-900/50 text-fuchsia-100',
    system: 'bg-purple-900/50 text-purple-100',
    isLight: false,
    dial: 'from-rose-700 to-rose-900',
    dialInner: 'from-rose-800 to-rose-900',
    dialMark: 'bg-white'
  },
  sepia: {
    bg: 'bg-amber-50',
    primary: 'bg-amber-100',
    secondary: 'bg-amber-200',
    border: 'border-amber-200',
    text: 'text-amber-950',
    textSecondary: 'text-amber-800',
    textTertiary: 'text-amber-600',
    accent: '#a16207',
    accentRgb: '161,98,7',
    pageRgb: '254,247,237',
    cardBgRgb: '254,243,219',
    surfaceRgb: '253,230,189',
    inactive: 'bg-amber-200',
    hover: 'hover:bg-amber-50',
    name: 'Sepia',
    djOne: 'bg-red-100 text-red-800',
    djTwo: 'bg-emerald-100 text-emerald-800',
    system: 'bg-orange-100 text-orange-800',
    isLight: true,
    dial: 'from-amber-300 to-amber-500',
    dialInner: 'from-amber-400 to-amber-600',
    dialMark: 'bg-amber-950'
  }
};

// AI Radio Stations - now represents WebSocket channels
const stations = [
  { 
    freq: 96.3, 
    name: "Ada & Ved", 
    artist: "Live Radio", 
    track: "Talk Show",
    description: "Live radio with AI DJs who can interact with callers",
    aiModel: "Gemini 2.0 Flash"
  }
];

export default function AIRadio() {
  // Persisted preferences (volume / mute / theme) are read from localStorage
  // on mount; play-state always starts paused because browsers block
  // autoplay without a user gesture.
  const readPref = (key, fallback) => {
    try {
      const v = localStorage.getItem(key);
      return v === null ? fallback : JSON.parse(v);
    } catch {
      return fallback;
    }
  };

  // State management
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(() => readPref("radio.volume", 75));
  const [isMuted, setIsMuted] = useState(() => readPref("radio.muted", false));
  const [frequency, setFrequency] = useState(96.3);
  // Dial covers 87.5 to 107.5 MHz across -150° to +150° (20 MHz / 300°).
  // This calibration makes the major ticks at i=0,5,10,15,20 land on round
  // 5 MHz boundaries (87.5, 92.5, 97.5, 102.5, 107.5), so the labels and
  // the tuner indicator both line up with the same angular positions.
  const [dialRotation, setDialRotation] = useState(((96.3 - 87.5) / 20) * 300 - 150);
  const currentStation = "Ada & Ved";
  const currentTrack = "The Talk Show";
  const [showInfo, setShowInfo] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipContent, setTooltipContent] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [colorTheme, setColorTheme] = useState(() => readPref("radio.theme", "cyber"));
  const [showThemeSettings, setShowThemeSettings] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [currentSpeaker, setCurrentSpeaker] = useState("system");
  const [isReconnecting, setIsReconnecting] = useState(false);

  // Transcript panel state
  const [showTranscriptPanel, setShowTranscriptPanel] = useState(false);
  const [transcriptHistory, setTranscriptHistory] = useState([]);
  
  // Dial interaction state
  const [isDragging, setIsDragging] = useState(false);

  // Refs
  const dialRef = useRef(null);
  const canvasRef = useRef(null);
  const wsManagerRef = useRef(null);
  const audioUtilsRef = useRef(null);
  const visualizerRef = useRef(null);
  const transcriptEndRef = useRef(null);
  // Aurora: full-bleed canvas rendering layered sine curves. Audio level
  // smoothly inflates the wave amplitude so the curtains swell with the
  // broadcast without being noisy.
  const auroraCanvasRef = useRef(null);
  // Mirror theme state into refs so the rAF draw loop can read the latest
  // accent + light-mode flag each frame without re-subscribing on every
  // theme change. getComputedStyle in a tight loop forces layout, so we
  // pass the values directly instead.
  const auroraAccentRef = useRef(themes[colorTheme].accentRgb);
  const auroraIsLightRef = useRef(!!themes[colorTheme].isLight);
  useEffect(() => {
    auroraAccentRef.current = themes[colorTheme].accentRgb;
    auroraIsLightRef.current = !!themes[colorTheme].isLight;
  }, [colorTheme]);
  // Holds the listener pair currently attached for a drag, so we can remove
  // the EXACT same function references that were added. Without this, React
  // re-renders between mousedown and mouseup leave document-level listeners
  // attached forever (each drag adds a new one).
  const dragListenersRef = useRef(null);

  // Queue of speaker / transcript events tagged with the cumulative
  // bytesEnqueued at arrival time. Drained by a polling effect once
  // bytesPlayed catches up. Keeps the UI synced with the listener's
  // actual playback position rather than the server's stream position.
  const pendingEventsRef = useRef([]);

  // Get the current theme
  const theme = themes[colorTheme];

  // Initialize WebSocket connection
  useEffect(() => {
    // Create the WebSocket manager if it doesn't exist
    if (!wsManagerRef.current) {
      const wsUrl = process.env.REACT_APP_WS_URL || 'ws://localhost:8000/ws';

      wsManagerRef.current = new WebSocketManager(wsUrl, {
        maxReconnectAttempts: 10
      });
      
      // Set up listeners
      wsManagerRef.current.addListener('open', () => {
        setIsConnected(true);
        setIsReconnecting(false);
        // connected
      });
      
      wsManagerRef.current.addListener('message', (event) => {
        // Binary frames are raw PCM16 LE mono samples from the broadcast.
        // AudioUtils tracks its own shouldAutoPlay; don't pass isPlaying
        // from this closure -- it would be frozen at the value captured
        // when the listener was registered (always false here).
        if (event.data instanceof ArrayBuffer) {
          if (audioUtilsRef.current) {
            audioUtilsRef.current.pushPCM(event.data);
          }
          return;
        }

        // Text frames are JSON control messages.
        let message;
        try {
          message = JSON.parse(event.data);
        } catch (err) {
          console.error("Error parsing WebSocket text message:", err);
          return;
        }

        // Cap the pending queue so a paused tab (where bytesPlayed stops
        // advancing) doesn't accumulate events without bound. The worklet
        // ring is capped at 60s of audio, so a queue >300 entries already
        // represents far more than the playable backlog.
        const enqueuePending = (ev) => {
          const q = pendingEventsRef.current;
          q.push(ev);
          if (q.length > 300) q.splice(0, q.length - 300);
        };

        switch (message.type) {
          case "hello":
            // Quiet -- just for diagnostics.
            break;

          case "speaker": {
            // Tag with the current bytes-enqueued so the drain effect can
            // apply this at the moment the corresponding audio actually
            // reaches the listener.
            const eta = audioUtilsRef.current ? audioUtilsRef.current.getBytesEnqueued() : 0;
            enqueuePending({ eta, type: "speaker", name: message.name });
            break;
          }

          case "transcript": {
            if (!message.text || !message.final) break;
            const eta = audioUtilsRef.current ? audioUtilsRef.current.getBytesEnqueued() : 0;
            enqueuePending({ eta, type: "transcript", text: message.text, speaker: message.speaker });
            break;
          }

          default:
            break;
        }
      });
      
      wsManagerRef.current.addListener('error', (error) => {
        console.error("WebSocket error:", error);
      });

      wsManagerRef.current.addListener('close', () => {
        setIsConnected(false);
      });

      wsManagerRef.current.addListener('reconnecting', () => {
        setIsReconnecting(true);
      });
    }
    
    // Connect to the WebSocket
    wsManagerRef.current.connect();
    
    // Initialize audio utils
    audioUtilsRef.current = new AudioUtils();
    audioUtilsRef.current.setVolume(volume, isMuted);
    
    // Clean up function
    return () => {
      if (wsManagerRef.current) {
        wsManagerRef.current.disconnect();
      }

      if (visualizerRef.current) {
        visualizerRef.current.stop();
      }

      // Tear down the AudioContext (and implicitly the worklet + analyser).
      // Browsers cap ~6 contexts per origin; without this, dev StrictMode
      // double-mounts or repeated mounts during HMR would leak them.
      const au = audioUtilsRef.current;
      if (au && au.audioContext && au.audioContext.state !== "closed") {
        au.audioContext.close().catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Transcript handling. Only finalised turns are added; streaming deltas
  // would be out-of-sync with the buffered audio playback.
  const addTranscriptMessage = (text, speaker = 'system') => {
    setTranscriptHistory(prev => [
      ...prev,
      {
        text,
        speaker,
        timestamp: new Date().toLocaleTimeString()
      }
    ].slice(-50)); // Keep only last 50 messages
  };

  // Initialize visualizer once after first mount. canvasRef.current is set
  // by the render that precedes this effect, so [] deps is correct -- the
  // previous [canvasRef.current] was a no-op (React doesn't observe ref
  // mutations) and only worked by accident.
  useEffect(() => {
    if (canvasRef.current && !visualizerRef.current) {
      visualizerRef.current = new VisualizerUtils(canvasRef.current, colorTheme, themes);
      // The analyser may not exist yet (created lazily by AudioUtils on first
      // user-gesture). The hook-up effect below picks it up when available.
      const a = audioUtilsRef.current && audioUtilsRef.current.getAnalyser();
      if (a) visualizerRef.current.setAnalyser(a);

      visualizerRef.current.start(
        isPlaying,
        isConnected,
        isConnected,
        currentSpeaker,
        audioUtilsRef.current?.isPlaying() || false
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After play is first pressed (which materialises the AudioContext and
  // analyser), attach it to the visualizer.
  useEffect(() => {
    if (!isPlaying) return;
    const a = audioUtilsRef.current && audioUtilsRef.current.getAnalyser();
    if (a && visualizerRef.current) {
      visualizerRef.current.setAnalyser(a);
    }
  }, [isPlaying]);

  // Update visualizer when relevant state changes
  useEffect(() => {
    if (visualizerRef.current) {
      visualizerRef.current.updateTheme(colorTheme);
      
      const hasSignal = getCurrentStationInfo() !== null;
      // No need to restart, just the parameters will be picked up on next frame
      visualizerRef.current.start(
        isPlaying,
        isConnected,
        hasSignal,
        currentSpeaker,
        audioUtilsRef.current?.isPlaying() || false
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, isConnected, colorTheme, currentSpeaker]);

  // Scroll to the bottom of the transcript when new messages arrive
  useEffect(() => {
    if (transcriptEndRef.current && showTranscriptPanel) {
      transcriptEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [transcriptHistory, showTranscriptPanel]);

  // Update audio state when volume changes
  useEffect(() => {
    if (audioUtilsRef.current) {
      audioUtilsRef.current.setVolume(volume, isMuted);
    }
  }, [volume, isMuted]);

  // Drain the pending speaker/transcript queue when the audio worklet's
  // playback position catches up. Polled at 20Hz, which is more than
  // sufficient given speaker changes happen every several seconds.
  useEffect(() => {
    const id = setInterval(() => {
      const audio = audioUtilsRef.current;
      if (!audio) return;
      const played = audio.getBytesPlayed();
      const q = pendingEventsRef.current;
      while (q.length > 0 && q[0].eta <= played) {
        const ev = q.shift();
        if (ev.type === "speaker") {
          setCurrentSpeaker(ev.name);
        } else if (ev.type === "transcript") {
          addTranscriptMessage(ev.text, ev.speaker);
        }
      }
    }, 50);
    return () => clearInterval(id);
  }, []);

  // Aurora animation. One rAF loop renders 5 layered sine curves filled
  // with a top-fading vertical gradient in the theme accent color. The
  // motion is fully autonomous -- it doesn't read audio. The visualizer
  // is the audio-reactive surface; the backdrop is its own ambient thing.
  // Additive ('screen') blending on dark themes, 'multiply' on light, so
  // the same code reads as a glow on black and a pastel wash on white.
  useEffect(() => {
    const canvas = auroraCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    let raf;
    let dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const sizeCanvas = () => {
      dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    sizeCanvas();
    window.addEventListener('resize', sizeCanvas);

    // Layers in normalised coords. yPct is vertical center of the curve
    // as a fraction of canvas height. Different freqs/speeds/signs
    // interleave so the layers never line up identically.
    const layers = [
      { freq: 0.0026, speed:  0.00009, amp: 70,  yPct: 0.38, alpha: 0.18 },
      { freq: 0.0034, speed: -0.00012, amp: 95,  yPct: 0.52, alpha: 0.15 },
      { freq: 0.0021, speed:  0.00007, amp: 120, yPct: 0.66, alpha: 0.13 },
      { freq: 0.0042, speed: -0.00014, amp: 85,  yPct: 0.78, alpha: 0.12 },
      { freq: 0.0030, speed:  0.00005, amp: 100, yPct: 0.90, alpha: 0.10 },
    ];

    const draw = (t) => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const accent = auroraAccentRef.current;
      const isLight = auroraIsLightRef.current;

      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = isLight ? 'multiply' : 'screen';

      for (const layer of layers) {
        const phase = t * layer.speed;
        const yBase = h * layer.yPct;

        // Three sines at related freqs for organic, non-repeating shapes.
        // Step of 6px stays smooth at typical viewport widths while
        // keeping the per-frame work bounded.
        ctx.beginPath();
        ctx.moveTo(-20, h + 20);
        for (let x = -20; x <= w + 20; x += 6) {
          const y = yBase
            + Math.sin(x * layer.freq + phase) * layer.amp
            + Math.sin(x * layer.freq * 2.3 + phase * 1.5) * layer.amp * 0.35
            + Math.sin(x * layer.freq * 0.5 + phase * 0.7) * layer.amp * 0.25;
          ctx.lineTo(x, y);
        }
        ctx.lineTo(w + 20, h + 20);
        ctx.closePath();

        // Vertical gradient: brightest near the curve top, fading down.
        const grad = ctx.createLinearGradient(0, yBase - layer.amp * 1.5, 0, h);
        grad.addColorStop(0.0, `rgba(${accent}, 0)`);
        grad.addColorStop(0.25, `rgba(${accent}, ${layer.alpha.toFixed(3)})`);
        grad.addColorStop(1.0, `rgba(${accent}, ${(layer.alpha * 0.35).toFixed(3)})`);
        ctx.fillStyle = grad;
        ctx.fill();
      }

      ctx.globalCompositeOperation = 'source-over';

      if (!reduceMotion) {
        raf = requestAnimationFrame(draw);
      }
    };

    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', sizeCanvas);
    };
  }, []);

  // Persist user preferences across reloads.
  useEffect(() => {
    try { localStorage.setItem("radio.volume", JSON.stringify(volume)); } catch {}
  }, [volume]);
  useEffect(() => {
    try { localStorage.setItem("radio.muted", JSON.stringify(isMuted)); } catch {}
  }, [isMuted]);
  useEffect(() => {
    try { localStorage.setItem("radio.theme", JSON.stringify(colorTheme)); } catch {}
  }, [colorTheme]);

  // Effect to handle audio playback state when isPlaying changes
  useEffect(() => {
    if (audioUtilsRef.current) {
      if (isPlaying) {
        audioUtilsRef.current.resume();
      } else {
        audioUtilsRef.current.suspend();
      }
    }
  }, [isPlaying]);

  // Handle keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case " ":
          // Space to toggle play/pause
          togglePlay();
          e.preventDefault();
          break;
        case "ArrowUp":
          // Up arrow to increase volume
          setVolume(prev => Math.min(100, prev + 5));
          if (isMuted) setIsMuted(false);
          e.preventDefault();
          break;
        case "ArrowDown":
          // Down arrow to decrease volume
          setVolume(prev => Math.max(0, prev - 5));
          e.preventDefault();
          break;
        case "m":
          // M to toggle mute
          toggleMute();
          e.preventDefault();
          break;
        case "t":
          // T to toggle theme settings
          setShowThemeSettings(!showThemeSettings);
          setShowTranscriptPanel(false);
          setShowInfo(false);
          e.preventDefault();
          break;
        case "l":
          // L to toggle transcript panel
          setShowTranscriptPanel(!showTranscriptPanel);
          setShowThemeSettings(false);
          setShowInfo(false);
          e.preventDefault();
          break;
        case "i":
          // I to toggle info panel
          setShowInfo(!showInfo);
          setShowThemeSettings(false);
          setShowTranscriptPanel(false);
          e.preventDefault();
          break;
        case "Escape":
          if (showThemeSettings) {
            setShowThemeSettings(false);
          } else if (showTranscriptPanel) {
            setShowTranscriptPanel(false);
          } else if (showInfo) {
            setShowInfo(false);
          }
          e.preventDefault();
          break;
        default:
          break;
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showThemeSettings, showTranscriptPanel, showInfo, isMuted, isPlaying]);

  // Toggle play/pause - affects audio playback
  const togglePlay = () => {
    // Initialize audio context on user interaction (required by browsers)
    if (audioUtilsRef.current) {
      audioUtilsRef.current.userInteractionHandler();
    }
    
    const newPlayState = !isPlaying;
    setIsPlaying(newPlayState);

    if (audioUtilsRef.current) {
      if (newPlayState) audioUtilsRef.current.resume();
      else audioUtilsRef.current.suspend();
    }
  };

  // Toggle mute
  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  // Get current station info
  const getCurrentStationInfo = () => {
    return stations.find(station => Math.abs(station.freq - frequency) < 0.3) || null;
  };

  // Handle dial rotation with improved control
  const handleDialDrag = (e) => {
    if (!dialRef.current) return;
    e.preventDefault(); // Prevent default behaviors
    
    const dial = dialRef.current;
    const rect = dial.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    // Calculate angle with better precision
    const deltaX = clientX - centerX;
    const deltaY = clientY - centerY;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // Only respond to drag if the user is dragging far enough from center
    // This prevents jumpy behavior from small movements near the center
    if (distance < 20) return;
    
    const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
    
    // Add 90 degrees to make the top of the dial 0 degrees
    let adjustedAngle = angle + 90;
    if (adjustedAngle > 180) adjustedAngle -= 360;
    
    // Limit the angle to the range -150 to 150 degrees
    const limitedAngle = Math.max(-150, Math.min(150, adjustedAngle));
    
    // Update the dial rotation
    setDialRotation(limitedAngle);
    
    // Calculate frequency based on rotation angle. The dial is decorative
    // in this build -- there's only one station, audio plays regardless --
    // so we update the visible frequency number but don't drive the station
    // labels or connection status off it. Subtle haptic feedback fires when
    // a "station marker" is crossed for tactile interest.
    const newFrequency = 87.5 + ((limitedAngle + 150) / 300) * 20;
    const roundedFrequency = Math.round(newFrequency * 10) / 10;
    setFrequency(roundedFrequency);

    const closest = stations.reduce((prev, curr) => {
      return Math.abs(curr.freq - roundedFrequency) < Math.abs(prev.freq - roundedFrequency) ? curr : prev;
    });
    if (Math.abs(closest.freq - roundedFrequency) < 0.3 && currentStation !== closest.name) {
      if (window.navigator?.vibrate) {
        window.navigator.vibrate(50);
      }
    }
  };

  // Show station tooltip on hover
  const handleStationHover = (station, event) => {
    // Get position for tooltip
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({ 
      x: rect.left, 
      y: rect.top - 40 // Position above the station marker
    });
    setTooltipContent(`${station.name} (${station.freq})`);
    setShowTooltip(true);
  };

  // handleMouseDown captures the *current render's* handleDialDrag into
  // dragListenersRef so the matching removeEventListener calls always pass
  // the same function identity. Previously, handleDialDrag was a fresh
  // closure each render: any React re-render between mousedown and mouseup
  // caused removeEventListener to be called with a different reference,
  // leaving the document-level listeners attached forever.
  const detachDragListeners = () => {
    const pair = dragListenersRef.current;
    if (!pair) return;
    document.removeEventListener("mousemove", pair.drag);
    document.removeEventListener("touchmove", pair.drag);
    document.removeEventListener("mouseup", pair.up);
    document.removeEventListener("touchend", pair.up);
    document.removeEventListener("touchcancel", pair.up);
    dragListenersRef.current = null;
  };

  const handleMouseDown = (e) => {
    e.preventDefault(); // Prevent default behaviors

    // Immediately handle the first position
    handleDialDrag(e);

    // Add active dragging state
    document.body.classList.add("dragging");
    setIsDragging(true);

    // Capture the exact references we attach so cleanup matches.
    const drag = handleDialDrag;
    const up = () => {
      detachDragListeners();
      document.body.classList.remove("dragging");
      setIsDragging(false);

      // Snap to nearest station if close enough
      const station = stations.find(s => Math.abs(s.freq - frequency) < 0.3);
      if (station) {
        setFrequency(station.freq);
        setDialRotation(((station.freq - 87.5) / 20) * 300 - 150);
      }
    };

    // Defensive: a prior drag's listeners shouldn't still be around, but if
    // they are (e.g. user clicked away and re-engaged), strip them first.
    detachDragListeners();
    dragListenersRef.current = { drag, up };

    document.addEventListener("mousemove", drag, { passive: false });
    document.addEventListener("touchmove", drag, { passive: false });
    document.addEventListener("mouseup", up, { once: true });
    document.addEventListener("touchend", up, { once: true });
    document.addEventListener("touchcancel", up, { once: true });
  };

  // Clean up any in-flight drag listeners on unmount.
  useEffect(() => {
    return () => {
      detachDragListeners();
      document.body.classList.remove("dragging");
    };
  }, []);

  // Signal strength is tied to connection only (dial is decorative).
  const getSignalStrength = () => (isConnected ? 100 : 0);

  // Radio info content
  const radioInfo = {
    title: "AI Radio",
    model: "A-001",
    description: "A live, always-on radio where every station is generated by AI in real time. Tune in and you join mid-broadcast — no script, no loop, no replay.",
    features: [
      "Live now: a talk show with two AI co-hosts.",
      "Every minute is generated live — never the same broadcast twice.",
      "Coming next: music stations and more formats.",
    ],
    manufacturer: "Akatski",
    year: "2026"
  };

  // Render theme settings panel. Each option is a tiny mock of the actual
  // UI (page bg + card surface + frequency tile + accent dot + waveform bars
  // + button) so the user can see what they'll get without applying it.
  const renderThemeSettings = () => {
    if (!showThemeSettings) return null;

    const renderPreview = (themeKey) => {
      const t = themes[themeKey];
      const isSelected = colorTheme === themeKey;
      return (
        <button
          key={themeKey}
          onClick={() => setColorTheme(themeKey)}
          className="theme-preview group relative rounded-xl overflow-hidden transition-all duration-200 focus:outline-none"
          style={{
            backgroundColor: `rgb(${t.pageRgb})`,
            border: isSelected
              ? `2px solid rgb(${t.accentRgb})`
              : t.isLight
                ? '2px solid rgba(15,23,42,0.06)'
                : '2px solid rgba(255,255,255,0.06)',
            boxShadow: isSelected
              ? `0 0 0 4px rgba(${t.accentRgb}, 0.15), 0 8px 20px -8px rgba(0,0,0,0.4)`
              : '0 4px 12px -6px rgba(0,0,0,0.3)',
            transform: isSelected ? 'translateY(-2px)' : 'none',
            aspectRatio: '5 / 4',
          }}
          aria-label={`Apply ${t.name} theme`}
          aria-pressed={isSelected}
        >
          {/* Mini card surface */}
          <div
            className="absolute inset-2 rounded-lg p-2 flex flex-col gap-1.5"
            style={{
              backgroundColor: `rgba(${t.cardBgRgb}, ${t.isLight ? 0.85 : 0.6})`,
              border: t.isLight
                ? '1px solid rgba(15,23,42,0.06)'
                : '1px solid rgba(255,255,255,0.06)',
            }}
          >
            {/* Frequency tile */}
            <div
              className="flex items-center justify-between rounded px-1.5 py-1"
              style={{ backgroundColor: `rgba(${t.surfaceRgb}, ${t.isLight ? 0.9 : 0.7})` }}
            >
              <span
                className="font-mono text-[8px] font-bold tracking-wider"
                style={{ color: t.isLight ? '#0f172a' : '#fff' }}
              >
                96.3
              </span>
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: `rgb(${t.accentRgb})`, boxShadow: `0 0 4px rgb(${t.accentRgb})` }}
              />
            </div>
            {/* Mini waveform */}
            <div className="flex items-end justify-center gap-[1px] flex-1 px-1">
              {[0.4, 0.7, 0.5, 0.9, 0.6, 0.8, 0.4, 0.7, 0.55, 0.85, 0.45, 0.65].map((h, i) => (
                <span
                  key={i}
                  className="w-[2px] rounded-full"
                  style={{
                    height: `${h * 100}%`,
                    backgroundColor: `rgba(${t.accentRgb}, ${0.35 + h * 0.5})`,
                  }}
                />
              ))}
            </div>
          </div>
          {/* Label */}
          <div
            className="absolute bottom-1 left-0 right-0 text-center text-[10px] font-medium"
            style={{ color: t.isLight ? '#475569' : 'rgba(255,255,255,0.7)' }}
          >
            {t.name}
          </div>
        </button>
      );
    };

    return (
      <div
        className="absolute inset-x-0 top-0 z-30 p-5 animate-fadeIn rounded-t-2xl"
        style={{
          backgroundColor: `rgba(${theme.cardBgRgb}, ${theme.isLight ? 0.95 : 0.85})`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: theme.isLight
            ? '1px solid rgba(15,23,42,0.06)'
            : '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <h3 className={`text-sm font-semibold mb-3 ${theme.text}`}>Choose a theme</h3>
        <div className="grid grid-cols-3 gap-2.5">
          {Object.keys(themes).map(renderPreview)}
        </div>
        <button
          onClick={() => setShowThemeSettings(false)}
          className={`mt-4 w-full py-2 text-xs font-medium ${theme.text} rounded-lg transition-colors`}
          style={{
            backgroundColor: theme.isLight ? 'rgba(15,23,42,0.05)' : 'rgba(255,255,255,0.06)',
          }}
        >
          Close
        </button>
      </div>
    );
  };

  // Render info panel as an overlay (frosted to match the new card).
  const renderInfoPanel = () => {
    if (!showInfo) return null;
    const dividerColor = theme.isLight ? 'rgba(15,23,42,0.06)' : 'rgba(255,255,255,0.06)';
    return (
      <div
        className="absolute inset-x-0 top-0 z-30 p-5 animate-fadeIn rounded-t-2xl"
        style={{
          backgroundColor: `rgba(${theme.cardBgRgb}, ${theme.isLight ? 0.95 : 0.85})`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${dividerColor}`,
        }}
      >
        <h3 className={`text-sm font-semibold mb-3 ${theme.text}`}>{radioInfo.title}</h3>
        <div
          className="rounded-xl overflow-y-auto p-4"
          style={{
            maxHeight: '60vh',
            backgroundColor: `rgba(${theme.surfaceRgb}, ${theme.isLight ? 0.7 : 0.4})`,
            border: `1px solid ${dividerColor}`,
          }}
        >
          <p className={`text-sm leading-relaxed mb-4 ${theme.textSecondary}`}>{radioInfo.description}</p>

          <div className="mb-4">
            <p className={`text-xs font-semibold mb-2 uppercase tracking-wider ${theme.textTertiary}`}>Features</p>
            <ul className="space-y-1.5">
              {radioInfo.features.map((feature, index) => (
                <li key={index} className={`text-sm flex gap-2 ${theme.textSecondary}`}>
                  <span
                    className="mt-2 inline-block w-1 h-1 rounded-full flex-shrink-0"
                    style={{ backgroundColor: theme.accent }}
                  />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          <div
            className={`text-[11px] font-mono ${theme.textTertiary} mt-4 pt-3 flex justify-between items-center`}
            style={{ borderTop: `1px solid ${dividerColor}` }}
          >
            <a
              href="https://akatski.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:opacity-80 transition-opacity"
              style={{ color: theme.accent, textDecorationColor: `${theme.accent}66` }}
            >
              {radioInfo.manufacturer}
            </a>
            <a
              href="https://x.com/aakashxsh"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="@aakashxsh on X"
              className="hover:opacity-80 transition-opacity inline-flex items-center"
              style={{ color: theme.accent }}
            >
              <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <a
              href="/about"
              className="underline underline-offset-2 hover:opacity-80 transition-opacity"
              style={{ color: theme.accent, textDecorationColor: `${theme.accent}66` }}
            >
              About
            </a>
          </div>
        </div>
        <button
          onClick={() => setShowInfo(false)}
          className={`mt-4 w-full py-2 text-xs font-medium ${theme.text} rounded-lg transition-colors`}
          style={{
            backgroundColor: theme.isLight ? 'rgba(15,23,42,0.05)' : 'rgba(255,255,255,0.06)',
          }}
        >
          Close
        </button>
      </div>
    );
  };

  // Render TranscriptPanel
  const renderTranscriptPanel = () => {
    return (
      <TranscriptPanel
        showPanel={showTranscriptPanel}
        onClose={() => setShowTranscriptPanel(false)}
        theme={theme}
        transcriptHistory={transcriptHistory}
      />
    );
  };

  return (
    <div className={`relative flex min-h-screen min-h-[100dvh] w-full items-center justify-center ${theme.bg} p-3 sm:p-5 overflow-hidden`}>
      {/* Aurora backdrop: layered sine curves rendered to a canvas. The
          rAF loop reads the latest accent + light flag off refs each frame
          so theme changes re-tint live without restarting the animation. */}
      <canvas
        ref={auroraCanvasRef}
        className="aurora-canvas pointer-events-none absolute inset-0 w-full h-full"
        aria-hidden="true"
      />

      <div
        className={`radio-card relative w-full max-w-md overflow-hidden rounded-2xl p-4 sm:p-7 ${theme.text} ${colorTheme === 'cyber' ? 'cyber-shadow' : ''}`}
        style={{
          backgroundColor: `rgba(${theme.cardBgRgb}, ${theme.isLight ? 0.82 : 0.55})`,
          backdropFilter: 'blur(24px) saturate(140%)',
          WebkitBackdropFilter: 'blur(24px) saturate(140%)',
          border: theme.isLight
            ? '1px solid rgba(15,23,42,0.08)'
            : '1px solid rgba(255,255,255,0.08)',
          boxShadow: theme.isLight
            ? '0 25px 70px -25px rgba(15,23,42,0.25), inset 0 1px 0 rgba(255,255,255,0.7)'
            : `0 30px 90px -25px rgba(0,0,0,0.7), 0 0 0 1px rgba(${theme.accentRgb},0.05), inset 0 1px 0 rgba(255,255,255,0.05)`,
        }}
      >
        {/* Theme settings overlay */}
        {renderThemeSettings()}
        
        {/* Info panel overlay */}
        {renderInfoPanel()}
        
        {/* Transcript panel */}
        {renderTranscriptPanel()}
        
        {/* Radio header */}
        <div className="mb-5 text-center relative">
          <h1 className={`font-mono text-xl sm:text-2xl font-bold tracking-[0.18em] ${theme.text} ${colorTheme === 'cyber' ? 'neon-text' : ''}`}>
            AI <span className={theme.textSecondary}>RADIO</span>
          </h1>
          <div className={`mt-1 text-[10px] font-mono uppercase tracking-[0.3em] ${theme.textTertiary}`}>Model A-001</div>

          {/* Connection status indicator - left */}
          <div className="absolute left-0 top-1 flex items-center gap-1.5">
            <span
              className="h-1.5 w-1.5 rounded-full transition-colors duration-300"
              style={{
                backgroundColor: isConnected
                  ? 'rgb(34,197,94)'
                  : isReconnecting
                    ? 'rgb(234,179,8)'
                    : 'rgb(239,68,68)',
                boxShadow: isConnected
                  ? '0 0 6px rgba(34,197,94,0.7)'
                  : isReconnecting
                    ? '0 0 6px rgba(234,179,8,0.7)'
                    : 'none',
                animation: isReconnecting ? 'speakerPulse 1.2s ease-in-out infinite' : 'none',
              }}
            />
            <span className={`text-[10px] font-mono uppercase tracking-widest ${theme.textTertiary}`}>
              PWR
            </span>
          </div>

          {/* Info button - right */}
          <button
            className={`absolute right-0 top-0 ${theme.textSecondary} transition-colors p-1.5 rounded-full hover:${theme.text}`}
            onClick={() => {
              setShowInfo(!showInfo);
              setShowThemeSettings(false);
              setShowTranscriptPanel(false);
            }}
            aria-label="Radio information"
          >
            <Info size={16} />
          </button>
        </div>
        
        {/* Frequency display */}
        <div className="mb-5 flex items-center justify-center relative z-10">
          <div
            className="relative flex h-16 sm:h-20 w-full items-center justify-center rounded-xl font-mono"
            style={{
              backgroundColor: `rgba(${theme.surfaceRgb}, ${theme.isLight ? 0.7 : 0.4})`,
              border: theme.isLight
                ? '1px solid rgba(15,23,42,0.06)'
                : '1px solid rgba(255,255,255,0.05)',
              boxShadow: theme.isLight
                ? 'inset 0 1px 2px rgba(15,23,42,0.04)'
                : 'inset 0 1px 0 rgba(255,255,255,0.04), inset 0 -1px 2px rgba(0,0,0,0.2)',
            }}
          >
            <div className={`absolute left-3 top-2 text-[10px] uppercase tracking-[0.2em] ${theme.textTertiary}`}>FM</div>
            <div
              className={`text-4xl sm:text-5xl font-bold tabular-nums tracking-tight ${theme.text}`}
              style={{
                fontVariantNumeric: 'tabular-nums',
                textShadow: colorTheme === 'cyber' ? '0 0 18px rgba(34,211,238,0.55)' : 'none',
              }}
            >
              {frequency.toFixed(1)}
            </div>
            <div className={`absolute bottom-2 right-3 text-[10px] uppercase tracking-[0.2em] ${theme.textTertiary}`}>MHz</div>

            {/* Signal strength indicator */}
            <div className="absolute top-2.5 right-3 flex items-center gap-1">
              <div
                className="h-1 w-12 rounded-full overflow-hidden"
                style={{ backgroundColor: theme.isLight ? 'rgba(15,23,42,0.08)' : 'rgba(255,255,255,0.08)' }}
              >
                <div
                  className="h-full transition-all duration-500 ease-out"
                  style={{
                    width: `${getSignalStrength()}%`,
                    backgroundColor: isConnected ? 'rgb(52, 211, 153)' : 'rgb(239, 68, 68)',
                    boxShadow: isConnected ? '0 0 8px rgba(52,211,153,0.5)' : 'none',
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Station info: show identity + live speaker status */}
        <div
          className="mb-5 rounded-xl p-4 text-center"
          style={{
            backgroundColor: `rgba(${theme.surfaceRgb}, ${theme.isLight ? 0.55 : 0.32})`,
            border: theme.isLight
              ? '1px solid rgba(15,23,42,0.05)'
              : '1px solid rgba(255,255,255,0.04)',
          }}
        >
          <div className={`text-lg font-semibold tracking-tight ${theme.text} ${colorTheme === 'cyber' ? 'neon-text' : ''}`}>
            {currentStation}
          </div>
          <div className={`text-[11px] uppercase tracking-[0.15em] mt-0.5 ${theme.textTertiary}`}>{currentTrack}</div>

          <div className="mt-2.5 flex items-center justify-center gap-1.5">
            {currentSpeaker === 'Ada' || currentSpeaker === 'Ved' ? (
              <>
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{
                    backgroundColor: currentSpeaker === 'Ada' ? 'rgb(248,113,113)' : 'rgb(74,222,128)',
                    boxShadow: `0 0 8px ${currentSpeaker === 'Ada' ? 'rgba(248,113,113,0.7)' : 'rgba(74,222,128,0.7)'}`,
                    animation: 'speakerPulse 1.4s ease-in-out infinite',
                  }}
                />
                <span
                  className="text-sm font-medium"
                  style={{ color: currentSpeaker === 'Ada' ? 'rgb(248,113,113)' : 'rgb(74,222,128)' }}
                >
                  {currentSpeaker} speaking
                </span>
              </>
            ) : (
              <span className={`text-sm font-medium ${theme.textTertiary}`}>Standby</span>
            )}
          </div>
        </div>
        
        {/* Theme-aware Dial. Outer wrapper kept at the larger size on
            mobile too -- at h-40 (160px), the 16px ring between the outer
            edge and the rotating knob was too tight for the frequency
            labels, which ended up obscured behind the inner knob. */}
        <div className="relative mb-6 flex items-center justify-center">
          <div
            className={`relative flex h-48 w-48 cursor-grab active:cursor-grabbing items-center justify-center rounded-full ${theme.primary} ${isDragging ? 'scale-102' : ''} transition-all duration-300 touch-none select-none`}
            onMouseDown={handleMouseDown}
            onTouchStart={handleMouseDown}
            role="slider"
            aria-valuemin={87.5}
            aria-valuemax={107.5}
            aria-valuenow={frequency}
            aria-label="Frequency dial"
            tabIndex={0}
            style={{
              boxShadow: isDragging 
                ? `0 0 20px ${colorTheme === 'cyber' ? 'rgba(5, 217, 232, 0.5)' : `${theme.accent}80`}, 0 0 10px ${colorTheme === 'cyber' ? 'rgba(5, 217, 232, 0.2)' : `${theme.accent}30`} inset`
                : `0 0 10px ${colorTheme === 'cyber' ? 'rgba(5, 217, 232, 0.3)' : `${theme.accent}40`}, 0 0 5px ${colorTheme === 'cyber' ? 'rgba(5, 217, 232, 0.1)' : `${theme.accent}20`} inset`
            }}
          >
            {/* Background glow effect */}
            <div className="absolute inset-0 rounded-full overflow-hidden">
              <div 
                className="absolute inset-0"
                style={{
                  background: `radial-gradient(circle at center, ${colorTheme === 'cyber' ? 'rgba(5, 217, 232, 0.1)' : `${theme.accent}10`} 0%, transparent 70%)`,
                  opacity: isDragging ? 0.8 : 0.4,
                  transition: 'opacity 0.3s ease'
                }}
              />
            </div>
            
            {/* Frequency markers. Two things to know:
                  - radius 86: ticks sit on the outer rim, just inside the
                    visible edge and outside the rotating knob (radius 64).
                  - the -90° offset converts the dial-angle convention used
                    by labels + station markers (0° = up, CW positive) into
                    the math-angle expected by `translate(R, 0)` (0° =
                    right). Without it, i=10 (97.5 MHz) would render at the
                    3-o'clock position instead of 12-o'clock and the whole
                    tick ring would feel rotated 90° from the labels. */}
            <div className="absolute inset-0">
              {Array.from({ length: 21 }).map((_, i) => {
                // Skip ticks at angular positions that already have a
                // frequency label rendered (87.5/97.5/107.5 at i=0/10/20)
                // -- the label stands in for the tick at those positions,
                // and stacking both reads as overlap rather than a dial
                // marking.
                if (i === 0 || i === 10 || i === 20) return null;

                const primaryMarkerColor = colorTheme === 'cyber' ? '#ec4899' : theme.accent;
                const secondaryMarkerColor = colorTheme === 'cyber' ? '#be185d' : `${theme.accent}70`;
                const dialAngle = -150 + i * 15;       // 0° = up, CW positive
                const cssRotation = dialAngle - 90;     // align with translate(R, 0)

                return (
                  <div
                    key={i}
                    className="absolute"
                    style={{
                      left: '50%',
                      top: '50%',
                      width: i % 5 === 0 ? '8px' : '4px',
                      height: i % 5 === 0 ? '2px' : '1px',
                      backgroundColor: i % 5 === 0 ? primaryMarkerColor : secondaryMarkerColor,
                      transform: `rotate(${cssRotation}deg) translate(86px, 0)`,
                      opacity: i % 5 === 0 ? 0.9 : 0.5,
                      boxShadow: i % 5 === 0 ? `0 0 5px ${primaryMarkerColor}80` : 'none',
                      transition: 'all 0.2s ease'
                    }}
                  />
                );
              })}
            </div>
                
            {/* Frequency labels. Centres are at radius ~80px from the dial
                centre (outer wrapper 192, inner knob 128 -- so the labels
                sit in the middle of the 32px ring with their corners
                clearing both the inner knob and the outer circle). */}
            {[
              { label: '87.5', left: '29%', top: '86%' },
              { label: '97.5', left: '50%', top: '8%'  },
              { label: '107.5', left: '71%', top: '86%' },
            ].map(({ label, left, top }) => (
              <div
                key={label}
                className={`absolute text-xs font-medium ${colorTheme === 'cyber' ? 'text-pink-500' : theme.text}`}
                style={{
                  left,
                  top,
                  transform: 'translate(-50%, -50%)',
                  textShadow: colorTheme === 'cyber'
                    ? '0 0 3px rgba(236, 72, 153, 0.7)'
                    : `0 0 3px ${theme.accent}40`,
                }}
              >
                {label}
              </div>
            ))}

            {/* Themed dial knob. Kept the same size (h-32 / 128px) at all
                breakpoints so the ring between knob and outer wrapper is a
                consistent 32px wide -- the frequency-label positioning
                math depends on that fixed ring width. */}
            <div
              ref={dialRef}
              className={`relative flex h-32 w-32 items-center justify-center rounded-full bg-gradient-to-br ${theme.dial} shadow-inner ${theme.isLight ? 'shadow-slate-500/50' : 'shadow-black/50'} transition-transform duration-300`}
              style={{
                transform: `rotate(${dialRotation}deg)`,
                boxShadow: isDragging
                  ? `0 0 20px ${theme.isLight ? 'rgba(100,116,139,0.6)' : 'rgba(0,0,0,0.6)'} inset, 0 0 8px ${colorTheme === 'cyber' ? 'rgba(236, 72, 153, 0.5)' : `${theme.accent}50`}`
                  : `0 0 15px ${theme.isLight ? 'rgba(100,116,139,0.5)' : 'rgba(0,0,0,0.5)'} inset`,
              }}
            >
              {/* Glowing themed border */}
              <div 
                className={`absolute h-full w-full rounded-full border-4 border-transparent ${
                  colorTheme === 'cyber' 
                    ? 'border-t-pink-500' 
                    : `border-t-current`
                } opacity-30`}
                style={{
                  borderTopColor: colorTheme === 'cyber' ? '#ec4899' : theme.accent,
                  boxShadow: `0 0 8px ${colorTheme === 'cyber' ? 'rgba(236, 72, 153, 0.5)' : `${theme.accent}50`}`,
                  animation: isDragging ? 'pulse 2s infinite' : 'none'
                }}
              />
              
              {/* Inner knob with themed grip */}
              <div className={`h-24 w-24 rounded-full bg-gradient-to-br ${theme.dialInner} shadow-inner ${theme.isLight ? 'shadow-slate-600/50' : 'shadow-black/50'} flex items-center justify-center`}>
                <div
                  className={`h-2 w-10 rounded-full ${theme.dialMark}`}
                  style={{
                    boxShadow: colorTheme === 'cyber'
                      ? '0 0 5px rgba(5, 217, 232, 0.8)'
                      : `0 0 5px ${theme.accent}80`
                  }}
                />
              </div>
            </div>
          </div>

          {/* Theme-aware station markers */}
          {stations.map((station) => {
            // Calculate position based on frequency
            const angle = ((station.freq - 87.5) / 20) * 300 - 150;
            const isActive = Math.abs(station.freq - frequency) < 0.3;
            
            // Determine colors based on theme
            const activeColor = colorTheme === 'cyber' ? '#22d3ee' : theme.accent;
            const inactiveColor = colorTheme === 'cyber' ? '#be185d' : `${theme.accent}55`;
            const activeShadow = colorTheme === 'cyber' 
              ? '0 0 10px #22d3ee, 0 0 5px #22d3ee inset' 
              : `0 0 10px ${theme.accent}, 0 0 5px ${theme.accent} inset`;
            const inactiveShadow = colorTheme === 'cyber'
              ? '0 0 5px rgba(236, 72, 153, 0.2)'
              : `0 0 3px ${theme.accent}30`;
            
            return (
              <div
                key={station.freq}
                className={`absolute h-3 w-3 rounded-full transition-all duration-300 ease-in-out cursor-pointer`}
                style={{
                  transform: `rotate(${angle}deg) translateY(-80px)`,
                  left: "calc(50% - 6px)",
                  top: "calc(50% - 6px)",
                  backgroundColor: isActive ? activeColor : inactiveColor,
                  boxShadow: isActive ? activeShadow : inactiveShadow,
                  zIndex: isActive ? 20 : 10,
                  opacity: isActive ? 1 : 0.7
                }}
                onMouseEnter={(e) => handleStationHover(station, e)}
                onMouseLeave={() => setShowTooltip(false)}
                onClick={() => {
                  setFrequency(station.freq);
                  setDialRotation(((station.freq - 87.5) / 20) * 300 - 150);
                }}
              />
            );
          })}
          
          {/* Theme-aware tooltip. Uses position: fixed because tooltipPosition
              is stored in viewport coordinates (from getBoundingClientRect),
              not relative to the dial parent. */}
          {showTooltip && (
            <div
              className={`fixed ${theme.secondary} ${theme.text} px-2 py-1 text-xs rounded z-50 animate-fadeIn pointer-events-none`}
              style={{
                left: `${tooltipPosition.x}px`,
                top: `${tooltipPosition.y - 5}px`,
                transform: 'translateX(-50%)',
                opacity: 0.95,
                boxShadow: colorTheme === 'cyber'
                  ? '0 0 8px rgba(5, 217, 232, 0.4), 0 0 4px rgba(5, 217, 232, 0.2) inset'
                  : `0 0 8px rgba(0,0,0,0.4), 0 0 3px ${theme.accent}30`,
                border: colorTheme === 'cyber'
                  ? '1px solid rgba(5, 217, 232, 0.3)'
                  : `1px solid ${theme.accent}30`
              }}
            >
              {tooltipContent}
            </div>
          )}
        </div>
        
        {/* Visualizer -- now a beefier centerpiece. Glow color tracks the
            active speaker so the panel feels reactive even when paused. */}
        <div
          className="mb-6 h-28 sm:h-32 overflow-hidden rounded-xl p-3 relative"
          style={{
            backgroundColor: `rgba(${theme.surfaceRgb}, ${theme.isLight ? 0.7 : 0.45})`,
            border: theme.isLight
              ? '1px solid rgba(15,23,42,0.06)'
              : '1px solid rgba(255,255,255,0.05)',
            boxShadow: (() => {
              const c = currentSpeaker === 'Ada'
                ? '248,113,113'
                : currentSpeaker === 'Ved'
                  ? '74,222,128'
                  : theme.accentRgb;
              return `inset 0 0 40px -10px rgba(${c}, 0.22), 0 0 30px -10px rgba(${c}, 0.18)`;
            })(),
            transition: 'box-shadow 0.6s ease',
          }}
        >
          <canvas ref={canvasRef} width={600} height={128} className="h-full w-full" />
        </div>
        
        {/* Call In (coming soon) + Transcript toggle */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <button
            type="button"
            disabled
            title="Call-in is coming soon"
            className={`relative flex items-center justify-center gap-2 px-3 py-3 rounded-xl ${theme.textTertiary} cursor-not-allowed transition-all`}
            style={{
              backgroundColor: `rgba(${theme.surfaceRgb}, ${theme.isLight ? 0.4 : 0.18})`,
              border: theme.isLight
                ? '1px dashed rgba(15,23,42,0.12)'
                : '1px dashed rgba(255,255,255,0.10)',
            }}
          >
            <PhoneCall size={16} />
            <span className="text-sm font-medium">Call In</span>
            <span
              className="absolute -top-1.5 -right-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-semibold"
              style={{
                backgroundColor: `rgba(${theme.accentRgb}, 0.15)`,
                color: `rgb(${theme.accentRgb})`,
                border: `1px solid rgba(${theme.accentRgb}, 0.3)`,
              }}
            >
              Soon
            </span>
          </button>

          <button
            type="button"
            onClick={() => {
              setShowTranscriptPanel(!showTranscriptPanel);
              setShowThemeSettings(false);
              setShowInfo(false);
            }}
            className={`relative flex items-center justify-center gap-2 px-3 py-3 rounded-xl ${theme.text} transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0 focus:outline-none`}
            style={{
              backgroundColor: showTranscriptPanel
                ? `rgba(${theme.accentRgb}, ${theme.isLight ? 0.15 : 0.18})`
                : `rgba(${theme.surfaceRgb}, ${theme.isLight ? 0.55 : 0.32})`,
              border: showTranscriptPanel
                ? `1px solid rgba(${theme.accentRgb}, 0.55)`
                : theme.isLight
                  ? '1px solid rgba(15,23,42,0.06)'
                  : '1px solid rgba(255,255,255,0.06)',
              boxShadow: showTranscriptPanel
                ? `0 0 0 3px rgba(${theme.accentRgb}, 0.12)`
                : 'none',
            }}
          >
            <MessageSquare size={16} />
            <span className="text-sm font-medium">Transcript</span>
            {showTranscriptPanel && (
              <span
                className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full"
                style={{
                  backgroundColor: `rgb(${theme.accentRgb})`,
                  boxShadow: `0 0 6px rgba(${theme.accentRgb}, 0.7)`,
                  animation: 'speakerPulse 1.6s ease-in-out infinite',
                }}
              />
            )}
          </button>
        </div>
        
        {/* Controls: play (primary, accent-tinted), volume, theme. */}
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={() => {
              if (audioUtilsRef.current) {
                audioUtilsRef.current.userInteractionHandler();
              }
              togglePlay();
            }}
            disabled={!isConnected}
            className={`relative flex h-14 w-14 items-center justify-center rounded-full transition-all duration-200 focus:outline-none ${
              !isConnected
                ? 'cursor-not-allowed opacity-60'
                : 'cursor-pointer transform hover:scale-105 active:scale-95'
            }`}
            style={{
              backgroundColor: isPlaying
                ? `rgb(${theme.accentRgb})`
                : `rgba(${theme.accentRgb}, ${theme.isLight ? 0.12 : 0.18})`,
              color: isPlaying
                ? (theme.isLight ? '#ffffff' : '#0a0a0a')
                : `rgb(${theme.accentRgb})`,
              border: `1px solid rgba(${theme.accentRgb}, ${isPlaying ? 0.4 : 0.35})`,
              boxShadow: isPlaying
                ? `0 0 0 4px rgba(${theme.accentRgb}, 0.18), 0 8px 20px -8px rgba(${theme.accentRgb}, 0.6)`
                : `0 4px 10px -4px rgba(${theme.accentRgb}, 0.3)`,
            }}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause size={22} /> : <Play size={22} className="ml-0.5" />}
            {isPlaying && (
              <span
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{
                  border: `2px solid rgba(${theme.accentRgb}, 0.4)`,
                  animation: 'playRing 2s ease-out infinite',
                }}
              />
            )}
          </button>

          <div className="flex items-center gap-3 flex-1">
            <button
              onClick={toggleMute}
              className={`${theme.textSecondary} transition-all duration-200 focus:outline-none rounded-full p-2`}
              style={{
                backgroundColor: 'transparent',
              }}
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>

            <div className="relative flex w-full touch-none select-none items-center">
              {(() => {
                const fillColor = `rgb(${theme.accentRgb})`;
                const trackColor = theme.isLight
                  ? 'rgba(15,23,42,0.10)'
                  : 'rgba(255,255,255,0.10)';
                const fillPct = isMuted ? 0 : volume;
                return (
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={isMuted ? 0 : volume}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value, 10);
                      setVolume(newValue);
                      if (newValue > 0 && isMuted) setIsMuted(false);
                    }}
                    className="radio-slider w-full appearance-none cursor-pointer"
                    style={{
                      backgroundImage: `linear-gradient(to right, ${fillColor} 0%, ${fillColor} ${fillPct}%, ${trackColor} ${fillPct}%, ${trackColor} 100%)`,
                      accentColor: fillColor,
                      '--slider-thumb-color': fillColor,
                    }}
                    aria-label="Volume control"
                  />
                );
              })()}
            </div>
          </div>

          <button
            onClick={() => {
              setShowThemeSettings(!showThemeSettings);
              setShowTranscriptPanel(false);
              setShowInfo(false);
            }}
            className={`flex h-14 w-14 items-center justify-center rounded-full ${theme.text} transition-all duration-200 transform hover:scale-105 active:scale-95 focus:outline-none`}
            style={{
              backgroundColor: showThemeSettings
                ? `rgba(${theme.accentRgb}, ${theme.isLight ? 0.15 : 0.2})`
                : `rgba(${theme.surfaceRgb}, ${theme.isLight ? 0.55 : 0.32})`,
              border: showThemeSettings
                ? `1px solid rgba(${theme.accentRgb}, 0.4)`
                : theme.isLight
                  ? '1px solid rgba(15,23,42,0.06)'
                  : '1px solid rgba(255,255,255,0.06)',
            }}
            aria-label="Theme settings"
          >
            <Palette size={20} />
          </button>
        </div>

        {/* Keyboard shortcuts help */}
        <div
          className={`mt-5 pt-4 text-[11px] ${theme.textTertiary} text-center font-mono tracking-wide`}
          style={{
            borderTop: theme.isLight ? '1px solid rgba(15,23,42,0.06)' : '1px solid rgba(255,255,255,0.05)',
          }}
        >
          SPACE play &middot; L transcript &middot; I info &middot; T themes
        </div>
      </div>
      
      {/* Component-local style block. Note: `jsx`/`global` props (styled-jsx)
          aren't supported in CRA and trigger React unknown-prop warnings, so
          this is a plain <style> tag -- the rules below still apply globally. */}
      <style>{`
        body.dragging {
          cursor: grabbing;
          user-select: none;
        }

        /* Aurora canvas. CSS blur softens the layered sine curves into
           glowing curtains rather than crisp graphics. */
        .aurora-canvas {
          z-index: 0;
          filter: blur(14px) saturate(120%);
          opacity: 0.95;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.35s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
        }

        @keyframes pulse {
          0% { opacity: 0.3; }
          50% { opacity: 0.6; }
          100% { opacity: 0.3; }
        }
        .animate-pulse {
          animation: pulse 2s infinite;
        }

        /* Soft attention pulse used for speaker dot + transcript-active dot. */
        @keyframes speakerPulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50%      { opacity: 1;   transform: scale(1.25); }
        }

        /* Concentric ring that emanates from the play button when active. */
        @keyframes playRing {
          0%   { transform: scale(1);    opacity: 0.7; }
          80%  { transform: scale(1.35); opacity: 0; }
          100% { transform: scale(1.35); opacity: 0; }
        }

        /* Volume slider: theme-coloured thumb + smooth track. */
        .radio-slider {
          height: 4px;
          border-radius: 9999px;
          outline: none;
        }
        .radio-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--slider-thumb-color, currentColor);
          border: 2px solid rgba(255, 255, 255, 0.85);
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
          cursor: pointer;
          transition: transform 0.15s ease;
        }
        .radio-slider:hover::-webkit-slider-thumb,
        .radio-slider:focus::-webkit-slider-thumb {
          transform: scale(1.15);
        }
        .radio-slider::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--slider-thumb-color, currentColor);
          border: 2px solid rgba(255, 255, 255, 0.85);
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
          cursor: pointer;
        }

        .transform {
          transition-property: transform;
        }
        .hover\\:scale-102:hover { transform: scale(1.02); }
        .hover\\:scale-105:hover { transform: scale(1.05); }
        .active\\:scale-95:active { transform: scale(0.95); }
        .scale-105 { transform: scale(1.05); }

        .neon-text {
          text-shadow: 0 0 10px currentColor, 0 0 20px currentColor;
        }
        .cyber-shadow {
          box-shadow:
            0 0 15px rgba(244, 114, 182, 0.25),
            0 0 30px rgba(34, 211, 238, 0.15),
            0 25px 80px -25px rgba(0,0,0,0.7);
        }

        /* Honour reduced-motion: the aurora rAF loop short-circuits itself
           when the OS pref is set. Slider thumb transition is also dropped
           so the only motion is what the user explicitly triggers. */
        @media (prefers-reduced-motion: reduce) {
          .radio-slider::-webkit-slider-thumb {
            transition: none !important;
          }
        }
      `}</style>
    </div>
  );
}